import React, { FC, useState, useEffect, memo } from 'react';
import classNames from 'classnames';

import usePlayerChangeController from 'application/hooks/usePlayerChangeController';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { PointParamNameAMP } from 'application/services/AnalyticsService/ampTypes';

import { DeviceTypes, PaymentKind } from 'domain/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';
import IconChevronRight from '@zvuk-b2b/react-uikit/ui/icons/ChevronRight';
import Input from '@zvuk-b2b/react-uikit/ui/Input';

import AnalyticsService, {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';

import { useBillingRepositoryInjector } from 'application/providers/DIContainerProvider';
import { useAccountCompanyId } from 'application/hooks';

import { TEXTS } from './texts';
import { useOnboardingContext } from '../../useOnboardingContext';
import { useOnboardingActionContext } from '../../useOnboardingActionContext';
import { SelectPlayerFlowType } from '../../types';

import styles from '../index.module.scss';

const BindDesktopModal: FC<ModalProps[ModalTypes.DESKTOP_PLAYER_BIND]> = ({ openModal, replaceModal, close }) => {
  const { flowType, currentPoint } = useOnboardingContext();
  const { setInFlow, clearCurrentPoint } = useOnboardingActionContext();
  const companyId = useAccountCompanyId();

  const { watchBillingInfo } = useBillingRepositoryInjector();
  const { hasInactiveLicenses, selectedPaymentKind } = watchBillingInfo(companyId);

  const [isInstructionExtanded, setIsInstructionExtanded] = useState(false);

  const isShowPaymentModal = () => {
    const hasDevice = Boolean(currentPoint?.device?.type);
    const hasStream = Boolean(currentPoint?.stream?.id);

    if (selectedPaymentKind === PaymentKind.Sbbol) {
      return hasInactiveLicenses && hasDevice && hasStream;
    }

    return currentPoint?.isEnabled === false && hasDevice && hasStream;
  };

  const onSuccessCallback = (deviceType: DeviceTypes) => {
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_PLAYER_TYPE_BOUND, {
      [StreamBindFlowParamNameAMP.PLAYER_TYPE]: deviceType,
      [PointParamNameAMP.POINT_ID]: currentPoint?.id,
    });

    setInFlow(false);

    if (flowType === SelectPlayerFlowType.CREATE_POINT || flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      replaceModal(ModalTypes.DEVICE_BIND_SUCCESS, {});

      return;
    }

    if (
      isShowPaymentModal() &&
      (flowType === SelectPlayerFlowType.FULL_ONBOARDING ||
        flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM)
    ) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      replaceModal(
        flowType === SelectPlayerFlowType.BIND_STREAM ||
          flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM
          ? ModalTypes.STREAM_BIND_SUCCESS
          : ModalTypes.DEVICE_BIND_SUCCESS,
        {}
      );
    }
  };

  const { bindingCode, setBindingCode, bindingCodeError, handleUpdateClick, updateDeviceLoading, setToPlayerType } =
    usePlayerChangeController({
      pointId: currentPoint?.id || '',
      fromPlayerType: DeviceTypes.Desktop,
      onSuccessCallback,
    });

  useEffect(() => {
    setToPlayerType('APP');
  }, []);

  const handleCancel = () => {
    if (flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      title={TEXTS.MODAL_TITLE}
      className={styles.DesktopPlayerFlowModal}
      contentClassName={styles.DesktopPlayerFlowModalContent}
      actionButtons={[
        <Button
          key="button_desktop_player_modal_bind_success"
          elementId="button_desktop_player_modal_bind_success"
          type={ButtonType.PRIMARY}
          isDisabled={bindingCode.length < 4}
          isLoading={updateDeviceLoading}
          onClick={handleUpdateClick}>
          {TEXTS.SUCCESS_BUTTON}
        </Button>,
        <Button
          key="button_desktop_player_modal_bind_cancel"
          elementId="button_desktop_player_modal_bind_cancel"
          type={ButtonType.SECONDARY}
          isLoading={updateDeviceLoading}
          onClick={() => {
            replaceModal(ModalTypes.DESKTOP_PLAYER_DOWNLOAD, {});
          }}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      <div>{TEXTS.TITLE_DESCRIPTION}</div>

      <Input
        elementId="desktop_player_modal_bind_code_input"
        onChange={setBindingCode}
        value={bindingCode}
        placeholder={TEXTS.CODE_BIND_PLACEHOLDER}
        maxLength={4}
        label={TEXTS.CODE_BIND_TITLE}
        error={bindingCodeError}
        containerClassName={styles.DesktopPlayerFlowModalInput}
      />

      <Button
        type={ButtonType.PLAIN}
        onClick={() => {
          setIsInstructionExtanded(!isInstructionExtanded);
        }}
        className={classNames(styles.DesktopPlayerFlowModalExpandButton, styles.DesktopPlayerFlowModalButton)}
        elementId="desktop_player_modal_expand_button">
        <span>{TEXTS.INSTALLATION_GUIDE_EXPAND_BUTTON}</span>
        <div className={styles.DesktopPlayerFlowModalExpandButtonIcon}>
          {isInstructionExtanded ? <IconChevronDown /> : <IconChevronRight />}
        </div>
      </Button>

      {isInstructionExtanded && (
        <ul data-testid="desktop_player_modal_installation_guide_list">
          <li>{TEXTS.INSTALLATION_GUIDE_ITEM_ONE}</li>
          <li>{TEXTS.INSTALLATION_GUIDE_ITEM_TWO}</li>
          <li>{TEXTS.INSTALLATION_GUIDE_ITEM_THREE}</li>
          <li>{TEXTS.INSTALLATION_GUIDE_ITEM_FOUR}</li>
        </ul>
      )}
    </ModalScreen>
  );
};

export default memo(BindDesktopModal);
